import '@gl/lit/components/base/collapsible/index.js';

import {html} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import styles from './style.scss';
import type {
  FlyErrorData,
  FlyErrorPart,
  RegisterErrorEvent,
} from '@gl/lit/utils/errors.js';
import {FlyErrorSeverity} from '@gl/lit/utils/errors.js';
import {repeat} from 'lit/directives/repeat.js';
import {v4} from 'uuid';
import {formatJSON, isJSON} from '@gl/lit/utils/strings.js';
import type {ClassInfo} from 'lit/directives/class-map.js';
import {classMap} from 'lit/directives/class-map.js';

export const DEBUG_TOOLS_ELEMENT_TAG = 'debug-tools';

export const REGISTER_ERROR_EVENT_TAG = 'debug:register-error';

/**
 * DebugTools
 */
@customElement(DEBUG_TOOLS_ELEMENT_TAG)
export default class DebugTools extends FlywheelComponent {
  @state()
  errors: FlyErrorData[] = [];

  @state()
  open = false;

  static get styles() {
    return [styles];
  }

  private get showErrorTick(): boolean {
    return !(
      this.errors.filter(e => e.severity !== FlyErrorSeverity.LOW).length === 0
    );
  }

  private partStyles(
    severity: FlyErrorSeverity,
    part: FlyErrorPart
  ): ClassInfo {
    return {
      'part--warning': severity === FlyErrorSeverity.MEDIUM,
      'part--critical': severity === FlyErrorSeverity.HIGH,
      'part--inline': !!part.inline,
    };
  }

  private errorStyles(data: FlyErrorData): ClassInfo {
    return {
      'error--warning': data.severity === FlyErrorSeverity.MEDIUM,
      'error--critical': data.severity === FlyErrorSeverity.HIGH,
    };
  }

  private formatErrorData(data: unknown): string {
    if (typeof data === 'string' && !isJSON(data)) {
      return data;
    }

    const d = typeof data === 'string' ? JSON.parse(data) : data;
    if (typeof d !== 'object' || d === null) {
      return formatJSON(data);
    }

    // Ensure ABV2 graphs are printed pretty.
    if (d['graph'] && typeof d['graph'] === 'string' && isJSON(d['graph'])) {
      d['graph'] = JSON.parse(d['graph']);
    }

    return formatJSON(data);
  }

  private async copy(data: FlyErrorData) {
    await navigator.clipboard.writeText(
      `
---
URL: ${location.href}
TIMESTAMP: ${data.timestamp}
---

${data.label} :: ${data.error.message}
${data.description || ''}

        ${data.error.parts.map(part => {
          return `
--- ${part.label} ---
${JSON.stringify(part.data)}
---
          `;
        })}
--- stacktrace ---
${data.error.stack}
--
      `
    );
  }

  beforeLoad() {
    addEventListener(REGISTER_ERROR_EVENT_TAG, (e: Event) => {
      // Hack for global custom event types.
      const registerEvent = e as RegisterErrorEvent;

      this.errors.unshift({
        ...registerEvent.detail,
        timestamp: new Date(),
      });

      this.requestUpdate();
    });
  }

  renderPart(severity: FlyErrorSeverity, part: FlyErrorPart) {
    return html`
      <div class="part ${classMap(this.partStyles(severity, part))}">
        ${!part.inline
          ? html`
              <fly-collapsible label=${part.label} variant="capsule" collapsed>
                <div class="part__data">
                  <textarea>${this.formatErrorData(part.data)}</textarea>
                </div>
              </fly-collapsible>
            `
          : html`
              <div class="part__inline">
                <strong>${part.label}</strong>:
                ${this.formatErrorData(part.data)}
              </div>
            `}
      </div>
    `;
  }

  renderError(data: FlyErrorData) {
    return html`
      <div class="error ${classMap(this.errorStyles(data))}">
        <div class="error__header">
          <div class="error__timestamp">${data.timestamp.toLocaleString()}</div>
          <div class="error__copy" @click=${() => this.copy(data)}>
            <fly-icon icon="content_copy"></fly-icon>
          </div>
        </div>
        <div class="error__label">${data.label}</div>
        ${data.description
          ? html`<div class="error__description"><${data.description}</div>`
          : ''}
        <div class="error__message">${data.error.message}</div>
        <div class="error__parts">
          ${repeat(
            data.error.parts,
            () => v4(),
            part => this.renderPart(data.severity, part)
          )}
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <button
        class="debug-drawer-trigger"
        @click=${() => {
          this.open = !this.open;
        }}
      >
        <fly-icon icon="pest_control"></fly-icon>
        ${this.showErrorTick ? html`<div class="debug-tick"></div>` : ''}
      </button>
      ${this.open
        ? html`<div class="debug-drawer">
            <div class="debug-header">
              <div class="debug-header__title">
                <img src="/assets/macgyver.png" />
                Debugger
              </div>
              <button
                class="debug-header__close"
                @click=${() => (this.open = false)}
              >
                <fly-icon icon="close"></fly-icon>
              </button>
            </div>
            <div class="debug-errors">
              ${repeat(
                this.errors,
                e => e.error.id,
                e => this.renderError(e)
              )}
              ${this.errors.length === 0
                ? html`
                    <div class="no-errors">
                      <div class="no-errors__party">🎉</div>
                      <h3>Well, what do you know?</h3>
                      <p>
                        Looks like you haven't encountered any errors...yet.
                      </p>
                    </div>
                  `
                : ''}
            </div>
          </div>`
        : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [DEBUG_TOOLS_ELEMENT_TAG]: DebugTools;
  }
}
